import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { deriveInitialMode } from './AgentSelectorHelpers';
import { useSelectionHistory } from './UseSelectionHistory';
import { useAgentMode } from './UseAgentMode';
import { useFetchAgents } from './UseFetchAgents';
import { useTagMatching } from './UseTagMatching';
import { SHOULD_DIRTY_TRUE } from '../../ServiceTestsFormControlConstants';
import { MULTI } from '../../../general/Constants';
import { usePreviousNotEmptyArray } from '../../../../../utilities/UsePreviousNotEmptyArray';
import { useTestTypeContext } from './UseTestTypeContext';
import useAgentsContext from './AgentsContextProvider';
import {
    ADAPTER_TYPE_FIELD_NAME,
    AGENT_ALL_FIELD_NAME,
    AGENT_FIELD_NAME,
    AGENT_IDS_FIELD_NAME,
    AGENT_MATCHALLTAGS_FIELD_NAME,
    AGENT_TAGS_FIELD_NAME,
    DEFAULT,
    TAG_AND,
    TAG_OR,
} from '../../ServiceTestsConstants';

/**
 * Orchestrates agent selector sub-hooks. Returns the same API shape as before.
 *
 * @state-ownership
 * - Owns: adapterType (useState), adapterTypeRef (useRef), tagOpRef (useRef)
 * - Delegates: mode → useAgentMode, availableAgents → useFetchAgents,
 *              tagOp/matchingTaggedAgents → useTagMatching, history → UseSelectionHistory
 * - Registers: RHF fields (agents.all, agents.ids, agents.tags, agents.matchAllTags, adapterType)
 */
export function useServiceTestAgentSelector() {
    const { watch, setValue, getValues, register } = useFormContext();
    const { testType } = useTestTypeContext();
    const { availableAgents } = useAgentsContext();

    // Stable refs for values that are stable-after-mount but ESLint cannot prove stable (QUAL-01 fix)
    const getValuesRef = useRef(getValues);
    getValuesRef.current = getValues;
    const setValueRef = useRef(setValue);
    setValueRef.current = setValue;
    const testTypeRef = useRef(testType);
    testTypeRef.current = testType;

    // Watched form values
    const agentsRaw = watch(AGENT_FIELD_NAME);
    const agentsValue = useMemo(() => agentsRaw || { all: true, ids: [], tags: [], matchAllTags: false }, [agentsRaw]);
    const agentsValueIds = agentsValue.ids;

    // Snapshot last selected not empty agents.ids version
    const previousNotEmptyAgentsIds = usePreviousNotEmptyArray(agentsValueIds);

    // Adapter type state (owned by orchestrator — triggers fetch effect)
    const adapterTypeRaw = watch(ADAPTER_TYPE_FIELD_NAME);
    const [adapterType, setAdapterType] = useState(() => adapterTypeRaw ?? DEFAULT);
    const adapterTypeRef = useRef(adapterType);
    adapterTypeRef.current = adapterType;

    // Orchestrator-owned tagOpRef — shared between useAgentMode (reads) and useTagMatching (writes)
    const tagOpRef = useRef(agentsRaw?.matchAllTags ? TAG_AND : TAG_OR);

    // Register ALL agents sub-fields and adapterType so setValue() can persist them
    useEffect(() => {
        register({ name: AGENT_ALL_FIELD_NAME });
        register({ name: AGENT_IDS_FIELD_NAME });
        register({ name: AGENT_TAGS_FIELD_NAME });
        register({ name: AGENT_MATCHALLTAGS_FIELD_NAME });
        register({ name: ADAPTER_TYPE_FIELD_NAME });
    }, [register]);

    // Mode + selection handlers
    const {
        mode,
        modeRef,
        handleModeChange,
        handleAddAgents,
        handleRemoveOne,
        handleBulkRemove,
        handleMutiAdapterTypeChange,
    } = useAgentMode({
        setValue,
        getValues,
        agentsValue,
        tagOpRef,
        previousNotEmptyAgentsIds,
        initialMode: deriveInitialMode(agentsValue),
    });

    // Selection history (save/restore on adapter switch)
    const onBeforeFetch = useSelectionHistory({
        getValuesRef,
        setValueRef,
        modeRef,
        previousNotEmptyAgentsIds,
    });

    // Agent fetching
    useFetchAgents({
        adapterType,
        testTypeRef,
        getValuesRef,
        setValueRef,
        onBeforeFetch,
    });

    // Selected agent objects (spans fetch + RHF domains — stays in orchestrator)
    const selectedAgentObjects = useMemo(() => {
        const ids = agentsValueIds || [];
        return availableAgents.filter((agent) => ids.includes(agent._id));
    }, [availableAgents, agentsValueIds]);

    // Tag matching
    const selectedTags = useMemo(() => agentsValue.tags || [], [agentsValue.tags]);
    const { tagOp, handleTagOpChange } = useTagMatching({
        mode,
        selectedTags,
        setValueRef,
        tagOpRef,
        initialTagOp: agentsRaw?.matchAllTags ? TAG_AND : TAG_OR,
    });

    // Adapter type change handler (cross-domain: touches orchestrator state).
    // Multi adapter ('Run only when part of a Wi-Fi Group Test') is transparent:
    // agent selection data is preserved so switching back restores the user's selection.
    // The UI hides the agent selector via isMultiAdapter flag, and the server preserves
    // agents data independently of adapterType (NGP-3487).
    const handleAdapterTypeChange = useCallback(
        (newAdapterType) => {
            setAdapterType(newAdapterType);
            setValue(ADAPTER_TYPE_FIELD_NAME, newAdapterType, SHOULD_DIRTY_TRUE);
            if (newAdapterType === MULTI) {
                handleMutiAdapterTypeChange();
            }
        },
        [handleMutiAdapterTypeChange, setValue]
    );

    return {
        mode,
        adapterType,
        selectedAgentObjects,
        tagOp,
        handleModeChange,
        handleAdapterTypeChange,
        handleAddAgents,
        handleRemoveOne,
        handleBulkRemove,
        handleTagOpChange,
    };
}
